
import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ItemAttributeForDisplay, ItemForDisplay, MetaTags } from '../../../models';
import { AppStateService, CartService, GoogleAnalyticsService, HttpErrorService, ItemService, SeoService } from '../../../services';
import { GAEventAction, PaymentType, PublicRoutes } from '../../../enums';
import { getItemPrice } from '../../../pipes';

/** when extending this class be sure to implement ngOnInit */
export abstract class NgxAimProductClass {
  // ngOnInit() {
  //   this.init();
  // }

  route = inject(ActivatedRoute);
  itemService = inject(ItemService);
  httpErrorService = inject(HttpErrorService);
  cartService = inject(CartService);
  messageService = inject(MessageService);
  dialogService = inject(DialogService);
  appStateService = inject(AppStateService);
  seoService = inject(SeoService);
  gaService = inject(GoogleAnalyticsService);

  params$ = this.route.paramMap.pipe(
    distinctUntilChanged(),
    takeUntilDestroyed(),
  );
  routeData$ = this.route.data.pipe(
    distinctUntilChanged(),
    takeUntilDestroyed(),
  );
  addRentalRef: DynamicDialogRef | undefined;
  item: ItemForDisplay | undefined;
  breadcrumbs: MenuItem[] = [];
  attributes: ItemAttributeForDisplay[] = [];
  isRental = false;
  showBuyButton = false;

  init() {
    this.showBuyButton = this.appStateService.paymentType === PaymentType.NONE ? false : true;

    this.params$.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.getProduct(+id);
      }
    });
    this.routeData$.subscribe((data) => {
      this.isRental = data['isRental'];
    });
  }

  getProduct(id: number) {
    this.itemService.findOne(id).subscribe({
      next: (item) => this.onSuccess(item),
      error: (err) => this.onError(err, 'Could not load product'),
    });
  }

  onError(err: any, message: string) {
    this.httpErrorService.onHttpError(err, message);
  }

  onSuccess(item: ItemForDisplay) {
    const attributesMap = new Map<string, string[]>();
    item.attributes.forEach((att) => {
      if (attributesMap.has(att.Name)) {
        attributesMap.set(att.Name, [...attributesMap.get(att.Name) || [], att.Value]);
      } else {
        attributesMap.set(att.Name, [att.Value]);
      }
    });

    const attributes: ItemAttributeForDisplay[] = [];
    for (const att of attributesMap.entries()) {
      attributes.push({
        Name: att[0],
        Values: att[1],
      });
    }
    this.attributes = attributes;

    this.item = item;
    this.breadcrumbs = [
      { label: 'Home', routerLink: '/' },
      { label: this.item.Manufacturer, routerLink: `/products/`, queryParams: { manufacturer: this.item.Manufacturer } },
      { label: this.item.Model }
    ];

    this.setSeoTags();
    this.emitGaEvent();
  }

  setSeoTags() {
    if (this.item) {
      const tags: MetaTags = {
        description: this.item.Description,
        titleOG: this.item.htmlMetaTitle,
        descriptionOG: this.item.Description,
        imageOG: this.item.images[0]?.thumbSrc ? `${this.appStateService.businessInfo.websiteUrl}/files/${this.item.images[0].thumbSrc}` : null,
        urlOG: `${this.appStateService.businessInfo.websiteUrl}/${PublicRoutes.PRODUCT}/${this.item.Model}/${this.item.Pk}`,
      }

      this.seoService.addSeoTags(tags);
      this.seoService.setTitle(this.item.htmlMetaTitle);
    }
  }

  emitGaEvent() {
    if (this.item) {
      this.gaService.emitEvent(GAEventAction.VIEW_ITEM, {
        item_id: this.item.Model,
        item_name: this.item.SETitle || this.item.Model,
        item_brand: this.item.Manufacturer,
        price: getItemPrice(this.item, this.appStateService.getUserPriceLevel())!,
      });
    }
  }

  onProductActionClick(item: ItemForDisplay) {
    if (!this.isRental) {
      this.addPurchaseToCart(item);
    }
  }

  addPurchaseToCart(item: ItemForDisplay) {
    if (item.HidePrice || item.AvailableQuantity <= 0) {
      return;
    }

    this.cartService.updateItemCartQuantity(item);
    this.messageService.add({
      severity: 'success',
      summary: `${item.Model} added to cart`,
      key: 'app-toast'
    });
  }
}
